import axios from 'axios';
import toast from 'react-hot-toast';
import { IS_DEVELOPMENT, API_URL } from '../config/environment';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 120000, // 120 seconds for long-running operations like SRO chat
  withCredentials: true, // Include cookies for authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log successful responses in development with FBR-aware logging
    if (IS_DEVELOPMENT) {
      const isFBRValidation = response.config.url?.includes('/fbr-validate-submit/process') || 
                              response.config.url?.includes('/validate-all') ||
                              response.config.url?.includes('/submit-all');
      
      if (isFBRValidation && response.data) {
        // For FBR validation/submission, show the actual FBR result
        const fbrSuccess = response.data.success && response.data.result?.success;
        const fbrStatusCode = response.data.result?.data?.validationResponse?.statusCode || 
                              response.data.result?.data?.submissionResponse?.statusCode;
        const fbrStatus = response.data.result?.data?.validationResponse?.status || 
                          response.data.result?.data?.submissionResponse?.status;
        
        if (fbrSuccess) {
          console.log('✅ FBR Validation SUCCESS:', {
            method: response.config.method?.toUpperCase(),
            url: response.config.url,
            httpStatus: response.status,
            fbrStatusCode: fbrStatusCode,
            fbrStatus: fbrStatus,
            message: response.data.message
          });
        } else {
          console.log('❌ FBR Validation FAILED:', {
            method: response.config.method?.toUpperCase(),
            url: response.config.url,
            httpStatus: response.status,
            fbrStatusCode: fbrStatusCode,
            fbrStatus: fbrStatus,
            message: response.data.message,
            error: response.data.result?.data?.validationResponse?.error || 
                   response.data.result?.data?.submissionResponse?.error
          });
        }
      } else {
        // For non-FBR requests, show regular API success
        console.log('✅ API Success:', {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status: response.status,
          data: response.data
        });
      }
    }
    return response;
  },
  (error) => {
    // Enhanced error logging
    const errorDetails = {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code
    };

    // Only log errors if it's not a network FBR call with 401 (these are expected when not authenticated)
    const isNetworkFBRCall = error.config?.url?.includes('/network/fbr-compliance') || 
                             error.config?.url?.includes('/network/verify-ip');
    const shouldSuppressLogging = isNetworkFBRCall && error.response?.status === 401;

    if (!shouldSuppressLogging) {
      console.error('❌ API Error Details:', errorDetails);

      // Log specific error information for debugging
      if (error.response?.data) {
        console.error('❌ API Error Response:', {
          error: error.response.data.error,
          message: error.response.data.message,
          details: error.response.data.details,
          configurationRequired: error.response.data.configurationRequired
        });
      }
    }

    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath === '/login' || currentPath === '/register';
      const isAuthCheck = error.config?.url?.includes('/auth/me');
      const isFBRCall = error.config?.url?.includes('/validate') || 
                       error.config?.url?.includes('/submit') || 
                       error.config?.url?.includes('/fbr') ||
                       error.config?.url?.includes('/network/fbr-compliance') ||
                       error.config?.url?.includes('/network/verify-ip');

      // Only log and handle 401 errors if it's not a suppressed network FBR call
      if (!shouldSuppressLogging) {
        console.log('❌ 401 Unauthorized - currentPath:', currentPath, 'isAuthPage:', isAuthPage, 'isAuthCheck:', isAuthCheck, 'isFBRCall:', isFBRCall);
      }

      // Only redirect if we're not already on auth pages, not an auth check, and not an FBR API call
      if (!isAuthPage && !isAuthCheck && !isFBRCall) {
        console.log('🔄 Redirecting to login...');
        // Use setTimeout to prevent immediate redirect loops
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      } else if (isFBRCall && !shouldSuppressLogging) {
        console.log('🚫 FBR API 401 error - NOT redirecting to login (FBR token issue)');
      }
    } else if (error.response?.status === 403) {
      toast.error('Access denied');
    } else if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    }

    // Don't show toast for certain errors that should be handled by components
    const skipToastErrors = [
      'FBR Token Not Configured',
      'FBR Authentication Failed'
    ];
    
    const shouldSkipToast = skipToastErrors.some(skipError => 
      error.response?.data?.error?.includes(skipError)
    );

    if (!shouldSkipToast && error.response?.status >= 400 && error.response?.status < 500) {
      const message = error.response?.data?.message || 'Request failed';
      if (!message.includes('Login failed') && !message.includes('Registration failed')) {
        toast.error(message);
      }
    }

    return Promise.reject(error);
  }
);

// API service functions
const api = {
  // Authentication
  auth: {
    login: (credentials) => apiClient.post('/auth/login', credentials),
    register: (userData) => apiClient.post('/auth/register', userData),
    logout: () => apiClient.post('/auth/logout'),
    getProfile: () => apiClient.get('/auth/me'),
    updateProfile: (data) => apiClient.put('/auth/profile', data),
    changePassword: (data) => apiClient.put('/auth/password', data),
  },

  // Invoices
  invoices: {
    create: (invoiceData) => apiClient.post('/invoice', invoiceData),
    getAll: (params = {}) => apiClient.get('/invoice', { params }),
    getById: (id) => apiClient.get(`/invoice/${id}`, {
      params: { _t: Date.now() } // Prevent caching
    }),
    update: (id, invoiceData) => apiClient.put(`/invoice/${id}`, invoiceData),
    updateStatus: (id, status) => apiClient.put(`/invoice/${id}/status`, { status }),
    delete: (id) => apiClient.delete(`/invoice/${id}`),
    move: (id, data) => apiClient.put(`/invoice/${id}/move`, data),
    // Deprecated direct invoice endpoints kept for fallback
    validate: (id) => apiClient.post(`/invoice/${id}/validate`),
    submit: (id) => apiClient.post(`/invoice/${id}/submit`),
    getStats: () => apiClient.get('/invoice/stats/summary'),

    // QR Code APIs
    generateQRCode: (id, params = {}) => apiClient.get(`/invoice/${id}/qrcode`, { params }),
    verifyQRCode: (data) => apiClient.post('/invoice/verify-qr', data),
    getQRSpecifications: () => apiClient.get('/invoice/qr-specifications'),

    // Bulk operations
    bulkSubmit: (invoices) => apiClient.post('/fbr-status/bulk-submit', { invoices }),
  },

  // FBR Validate/Submit APIs (new)
  fbrValidate: {
    validate: ({ invoiceData, invoiceId }) => apiClient.post('/fbr-validate/validate', { invoiceData, invoiceId }),
    submit: ({ invoiceData, invoiceId, validateFirst = true }) =>
      apiClient.post('/fbr-validate/submit', { invoiceData, invoiceId, validateFirst })
  },

  // Reference data
  reference: {
    getProvinces: () => apiClient.get('/reference/provinces'),
    getHSCodes: (params = {}) => apiClient.get('/reference/hs-codes', { params }),
    getUOMCodes: (params = {}) => apiClient.get('/reference/uom', { params }),
    getRegistrationTypes: () => apiClient.get('/reference/registration-types'),
    getInvoiceTypes: () => apiClient.get('/reference/invoice-types'),
    syncAll: () => apiClient.post('/reference/sync'),
    getStats: () => apiClient.get('/reference/stats'),

    // New FBR Reference APIs
    getDocumentTypes: () => apiClient.get('/reference/document-types'),
    getSROItems: () => apiClient.get('/reference/sro-items'),
    getTransactionTypes: () => apiClient.get('/reference/transaction-types'),
    getSROSchedule: (params) => apiClient.get('/reference/sro-schedule', { params }),
    getSaleTypeToRate: (params) => apiClient.get('/reference/sale-type-to-rate', { params }),
    getHSCodeUOM: (params) => apiClient.get('/reference/hs-code-uom', { params }),
    getSROItemsBySchedule: (params) => apiClient.get('/reference/sro-items-by-schedule', { params }),

    // STATL APIs - Updated to use new validation endpoints
    checkSTATLStatus: (data) => apiClient.post('/reference/validate-statl', data),
    checkRegistrationType: (data) => apiClient.post('/reference/validate-registration-type', data),

    // UoM Validation APIs
    validateUoM: (data) => apiClient.post('/reference/validate-uom', data),
    getUoMOptions: (hsCode) => apiClient.get(`/reference/uom-options/${hsCode}`),
    fixUoM: (data) => apiClient.post('/reference/fix-uom', data),
  },

  // Customers
  customers: {
    getAll: (params = {}) => apiClient.get('/customers', { params }),
    getById: (id) => apiClient.get(`/customers/${id}`),
    create: (customerData) => apiClient.post('/customers', customerData),
    update: (id, customerData) => apiClient.put(`/customers/${id}`, customerData),
    delete: (id) => apiClient.delete(`/customers/${id}`),
    getWalkIn: () => apiClient.get('/customers/walk-in/default'),
  },

  // Reports
  reports: {
    getInvoiceSummary: (params) => apiClient.get('/reports/invoice-summary', { params }),
    getInvoiceDetails: (params) => apiClient.get('/reports/invoice-details', { params }),
    getSalesByProvince: (params) => apiClient.get('/reports/sales-by-province', { params }),
    getMonthlyTrends: (params) => apiClient.get('/reports/monthly-trends', { params }),
    getTopCustomers: (params) => apiClient.get('/reports/top-customers', { params }),
    getTopProducts: (params) => apiClient.get('/reports/top-products', { params }),
    getHsCodeAnalysis: (params) => apiClient.get('/reports/hs-code-analysis', { params }),
    getTaxSummary: (params) => apiClient.get('/reports/tax-summary', { params }),
    getApiUsage: (params) => apiClient.get('/reports/api-usage', { params }),
    getUserActivity: (params) => apiClient.get('/reports/user-activity', { params }),
    exportReport: (reportType, params) => apiClient.get(`/reports/${reportType}/export`, {
      params,
      responseType: 'blob'
    })
  },

  // Scenarios
  scenarios: {
    getAll: () => apiClient.get('/scenarios'),
    getById: (id) => apiClient.get(`/scenarios/${id}`),
    generate: (id, data) => apiClient.post(`/scenarios/${id}/generate`, data),
    validate: (id, data) => apiClient.post(`/scenarios/${id}/validate`, data),
    submit: (id, data) => apiClient.post(`/scenarios/${id}/submit`, data),
    getStats: () => apiClient.get('/scenarios/stats/summary'),
    getAccessSummary: () => apiClient.get('/scenarios/access/summary')
  },

  // User Profile
  user: {
    getProfile: () => apiClient.get('/auth/profile'),
    updateProfile: (data) => apiClient.put('/auth/profile', data)
  },



  // Reference Data from Database
  referenceData: {
    getAll: () => apiClient.get('/reference-data'),
    getByCategory: (category) => apiClient.get(`/reference-data/${category}`),
    getByCategoryAndCode: (category, code) => apiClient.get(`/reference-data/${category}/${code}`)
  },

  // Canvas APIs
  canvas: {
    getScenarios: () => apiClient.get('/canvas/scenarios'),
    getScenario: (id) => apiClient.get(`/canvas/scenarios/${id}`),
    createScenario: (data) => apiClient.post('/canvas/scenarios', data),
    updateScenario: (id, data) => apiClient.put(`/canvas/scenarios/${id}`, data),
    deleteScenario: (id) => apiClient.delete(`/canvas/scenarios/${id}`),
    saveCanvas: (id, data) => apiClient.post(`/canvas/scenarios/${id}/canvas`, data),
    updateCanvas: (id, data) => apiClient.put(`/canvas/scenarios/${id}/canvas`, data),
    testScenario: (id, data) => apiClient.post(`/canvas/scenarios/${id}/test`, data),
    getTemplates: (id) => apiClient.get(`/canvas/scenarios/${id}/templates`),
    saveTemplate: (id, data) => apiClient.post(`/canvas/scenarios/${id}/templates`, data)
  },
  
  // Batch Invoices APIs
  batchInvoices: {
    getAll: () => apiClient.get('/batch-invoices'),
    getById: (id) => apiClient.get(`/batch-invoices/${id}`),
    upload: (formData) => apiClient.post('/batch-invoices/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
    delete: (id) => apiClient.delete(`/batch-invoices/${id}`),
    validateAll: (id) => apiClient.post(`/batch-invoices/${id}/validate-all`),
    submitAll: (id) => apiClient.post(`/batch-invoices/${id}/submit-all`),
    downloadExcel: (id) => apiClient.get(`/batch-invoices/${id}/download-excel`, { responseType: 'blob' }),
    downloadValidationReport: (id) => apiClient.get(`/batch-invoices/${id}/download-validation-report`, { responseType: 'blob' }),
    downloadSubmissionReport: (id) => apiClient.get(`/batch-invoices/${id}/download-submission-report`, { responseType: 'blob' })
  },

  // Folders
  folders: {
    create: (folderData) => apiClient.post('/folders', folderData),
    getAll: (params = {}) => apiClient.get('/folders', { params }),
    getById: (id) => apiClient.get(`/folders/${id}`),
    update: (id, folderData) => apiClient.put(`/folders/${id}`, folderData),
    delete: (id) => apiClient.delete(`/folders/${id}`),
  },

  // Community
  community: {
    listPosts: (params = {}) => apiClient.get('/community/posts', { params }),
    getPost: (id) => apiClient.get(`/community/posts/${id}`),
    createPost: (data) => apiClient.post('/community/posts', data),
    updatePost: (id, data) => apiClient.put(`/community/posts/${id}`, data),
    deletePost: (id) => apiClient.delete(`/community/posts/${id}`),
    addReply: (id, data) => apiClient.post(`/community/posts/${id}/replies`, data),
    deleteReply: (replyId) => apiClient.delete(`/community/replies/${replyId}`),
    toggleLike: (id) => apiClient.post(`/community/posts/${id}/like`),
    markSolved: (id) => apiClient.post(`/community/posts/${id}/mark-solved`),
    suggestTags: (data) => apiClient.post('/community/tags/suggest', data),
  },

  // SRO RAG
  sro: {
    upload: (file) => apiHelpers.uploadFile('/sro/upload', file),
    addFbrUrl: (payload) => apiClient.post('/sro/add-fbr-url', payload),
    list: () => apiClient.get('/sro/list'),
    chat: (payload) => apiClient.post('/sro/chat', payload),
  },

  // Direct HTTP methods for backward compatibility
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  delete: (url, config) => apiClient.delete(url, config)
};

// Helper functions for common operations
const apiHelpers = {
  // Handle API errors consistently
  handleError: (error, defaultMessage = 'An error occurred') => {
    const message = error.response?.data?.message || defaultMessage;
    const details = error.response?.data?.details;
    
    return {
      message,
      details,
      status: error.response?.status,
      data: error.response?.data
    };
  },

  // Format validation errors for display
  formatValidationErrors: (errors) => {
    if (!errors || !Array.isArray(errors)) return [];
    
    return errors.map(error => ({
      field: error.field,
      message: error.message,
      value: error.value
    }));
  },

  // Create query string from object
  createQueryString: (params) => {
    const searchParams = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        searchParams.append(key, params[key]);
      }
    });
    
    return searchParams.toString();
  },

  // Download file from API response
  downloadFile: (response, filename) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Upload file with progress
  uploadFile: async (url, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
  },
};

// Export both the client and the API functions
export { apiClient, api, apiHelpers };
export default api;
